import React from "react";
import { Box, Divider, Text } from "@chakra-ui/react";

/**
 * Minimal markdown renderer for the AI summary — supports headings (#..####),
 * bullet lists (- / *), horizontal rules (---) and inline **bold**. Intentionally
 * tiny so we don't pull in a markdown dependency for a single, well-formed field.
 */

function renderInline(text: string): React.ReactNode[] {
	// Split on **bold** spans, keeping the delimiters via a capture group.
	return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
		if (part.startsWith("**") && part.endsWith("**")) {
			return (
				<Text as="span" key={i} fontWeight={600}>
					{part.slice(2, -2)}
				</Text>
			);
		}
		return <React.Fragment key={i}>{part}</React.Fragment>;
	});
}

export function Markdown({ content }: { content: string }): JSX.Element {
	const blocks: React.ReactNode[] = [];

	content.split("\n").forEach((raw, i) => {
		const line = raw.trim();
		if (line === "") return;

		if (/^---+$/.test(line)) {
			blocks.push(<Divider key={i} my={3} />);
			return;
		}

		const heading = /^(#{1,4})\s+(.*)$/.exec(line);
		if (heading) {
			const level = heading[1].length;
			blocks.push(
				<Text key={i} fontWeight={700} fontSize={level <= 2 ? "md" : "sm"} mt={3} mb={1}>
					{renderInline(heading[2])}
				</Text>
			);
			return;
		}

		const bullet = /^[-*]\s+(.*)$/.exec(line);
		if (bullet) {
			blocks.push(
				<Box key={i} pl={4} mb={1}>
					<Text fontSize="sm">• {renderInline(bullet[1])}</Text>
				</Box>
			);
			return;
		}

		blocks.push(
			<Text key={i} fontSize="sm" mb={2}>
				{renderInline(line)}
			</Text>
		);
	});

	return <Box>{blocks}</Box>;
}
